if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();
        this.elements = {
          liveRegion: this.querySelector('[id^="GalleryStatus"]'),
          viewer: this.querySelector('[id^="GalleryViewer"]'),
          thumbnails: this.querySelector('[id^="GalleryThumbnails"]'),
        };
        this.mql = window.matchMedia('(min-width: 750px)');
        this.observeDeferredMediaSlides();
        // Don't rely solely on the IntersectionObserver's implicit first callback
        // to autoplay the initially-active slide's video: on a cold cache, layout
        // can still be shifting (images/fonts loading) when this constructor runs,
        // so that first callback can fire against a stale/zero measurement and
        // never get a follow-up — the video ends up needing a manual click.
        // Kick it off explicitly and deterministically instead.
        const activeSlide = this.elements.viewer.querySelector('[data-media-id].is-active');
        if (activeSlide) this.playActiveMedia(activeSlide);
        if (!this.elements.thumbnails) return;

        this.elements.viewer.addEventListener('slideChanged', debounce(this.onSlideChanged.bind(this), 500));
        this.elements.thumbnails.querySelectorAll('[data-target]').forEach((mediaToSwitch) => {
          mediaToSwitch
            .querySelector('button')
            .addEventListener('click', this.setActiveMedia.bind(this, mediaToSwitch.dataset.target, false));
        });
        if (this.dataset.desktopLayout.includes('thumbnail') && this.mql.matches) this.removeListSemantic();
      }

      onSlideChanged(event) {
        const thumbnail = this.elements.thumbnails.querySelector(
          `[data-target="${event.detail.currentElement.dataset.mediaId}"]`
        );
        this.setActiveThumbnail(thumbnail);
      }

      observeDeferredMediaSlides() {
        // Observe the <li> slide itself, not the nested .deferred-media: on mobile,
        // .product-media-container.media-fit-cover switches to display:flex/align-self:stretch
        // and its .media child to position:initial (section-main-product.css), which collapses
        // .deferred-media to a permanent 0x0 box — always 0 intersection no matter the scroll position.
        // The <li> keeps real layout (it's what the user actually sees and swipes).
        const slider = this.elements.viewer.querySelector('[id^="Slider-"]');
        const slides = slider
          ? Array.from(slider.querySelectorAll('li[data-media-id]')).filter((li) => li.querySelector('.deferred-media'))
          : [];
        if (!slides.length) return;

        const observer = new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              if (entry.intersectionRatio < 0.5) return;
              // Mirrors the .is-active bookkeeping setActiveMedia() does on thumbnail click:
              // on mobile, .product--thumbnail_slider .is-active .deferred-media is the CSS rule
              // that actually shows the video instead of the modal-opener poster overlay
              // (section-main-product.css) — without it the video plays hidden behind the poster.
              // Skips setActiveMedia()'s own scrollTo() calls since the user's swipe already
              // put this slide in view; forcing a scroll here would fight that gesture.
              this.elements.viewer.querySelectorAll('[data-media-id]').forEach((el) => el.classList.remove('is-active'));
              entry.target.classList.add('is-active');
              this.playActiveMedia(entry.target);
              if (this.elements.thumbnails) {
                this.setActiveThumbnail(this.elements.thumbnails.querySelector(`[data-target="${entry.target.dataset.mediaId}"]`));
              }
            });
          },
          { root: slider, threshold: 0.5 }
        );
        slides.forEach((li) => observer.observe(li));
      }

      setActiveMedia(mediaId, prepend) {
        const activeMedia =
          this.elements.viewer.querySelector(`[data-media-id="${mediaId}"]`) ||
          this.elements.viewer.querySelector('[data-media-id]');
        if (!activeMedia) {
          return;
        }
        this.elements.viewer.querySelectorAll('[data-media-id]').forEach((element) => {
          element.classList.remove('is-active');
        });
        activeMedia?.classList?.add('is-active');

        if (prepend) {
          activeMedia.parentElement.firstChild !== activeMedia && activeMedia.parentElement.prepend(activeMedia);

          if (this.elements.thumbnails) {
            const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
            activeThumbnail.parentElement.firstChild !== activeThumbnail && activeThumbnail.parentElement.prepend(activeThumbnail);
          }

          if (this.elements.viewer.slider) this.elements.viewer.resetPages();
        }

        this.preventStickyHeader();
        window.setTimeout(() => {
          if (!this.mql.matches || this.elements.thumbnails) {
            activeMedia.parentElement.scrollTo({ left: activeMedia.offsetLeft });
          }
          const activeMediaRect = activeMedia.getBoundingClientRect();
          // Don't scroll if the image is already in view
          if (activeMediaRect.top > -0.5) return;
          const top = activeMediaRect.top + window.scrollY;
          window.scrollTo({ top: top, behavior: 'smooth' });
        });
        this.playActiveMedia(activeMedia);

        if (!this.elements.thumbnails) return;
        const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
        this.setActiveThumbnail(activeThumbnail);
        this.announceLiveRegion(activeMedia, activeThumbnail.dataset.mediaPosition);
      }

      setActiveThumbnail(thumbnail) {
        if (!this.elements.thumbnails || !thumbnail) return;

        this.elements.thumbnails
          .querySelectorAll('button')
          .forEach((element) => element.removeAttribute('aria-current'));
        thumbnail.querySelector('button').setAttribute('aria-current', true);
        if (this.elements.thumbnails.isSlideVisible(thumbnail, 10)) return;

        this.elements.thumbnails.slider.scrollTo({ left: thumbnail.offsetLeft });
      }

      announceLiveRegion(activeItem, position) {
        const image = activeItem.querySelector('.product__modal-opener--image img');
        if (!image) return;
        image.onload = () => {
          this.elements.liveRegion.setAttribute('aria-hidden', false);
          this.elements.liveRegion.innerHTML = window.accessibilityStrings.imageAvailable.replace('[index]', position);
          setTimeout(() => {
            this.elements.liveRegion.setAttribute('aria-hidden', true);
          }, 2000);
        };
        image.src = image.src;
      }

      playActiveMedia(activeItem) {
        const deferredMedia = activeItem.querySelector('.deferred-media');
        if (deferredMedia && deferredMedia.getAttribute('loaded')) {
          const video = deferredMedia.querySelector('video');
          // Already playing: skip pause+replay, which would abort the in-flight play() for no reason
          // (happens when the IntersectionObserver reports several crossings in quick succession).
          if (video && !video.paused) return;
        }
        window.pauseAllMedia();
        if (!deferredMedia) return;
        if (deferredMedia.getAttribute('loaded')) {
          const video = deferredMedia.querySelector('video');
          if (video) video.play().catch(() => {});
        } else {
          deferredMedia.loadContent(false);
        }
      }

      preventStickyHeader() {
        this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
        if (!this.stickyHeader) return;
        this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
      }

      removeListSemantic() {
        if (!this.elements.viewer.slider) return;
        this.elements.viewer.slider.setAttribute('role', 'presentation');
        this.elements.viewer.sliderItems.forEach((slide) => slide.setAttribute('role', 'presentation'));
      }
    }
  );
}
